import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  normalizeCallMode,
  requiredMediaForMode,
  saveDeviceReadiness,
} from "../../util/deviceReadiness";
// Recording-indicator fix: this component's own getUserMedia() preview
// stream was only ever tracked in a local ref, cleaned up solely by this
// component's own unmount effect. If that unmount is ever skipped (e.g. a
// hard redirect elsewhere in the call-abort flow instead of a normal SPA
// route change), nothing else in the app knew this stream existed, so the
// browser tab's mic/camera indicator could stay lit indefinitely. Registering
// it here means the shared stopAllRegisteredMedia() safety net — already
// called on every call-end path and on Appointments list mount — will also
// catch and stop it even if this component's own cleanup did not run.
import { registerMediaStream } from "../../util/mediaTrackRegistry";

function stopStream(stream) {
  try {
    stream?.getTracks?.().forEach((track) => track.stop());
  } catch (_) {
    /* ignore */
  }
}

function mediaErrorMessage(error, needsVideo) {
  const name = error?.name || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError") {
    return needsVideo
      ? "Camera and microphone access is blocked. Allow both in your browser's site settings, then retry."
      : "Microphone access is blocked. Allow it in your browser's site settings, then retry.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return needsVideo
      ? "A working camera and microphone are required for this video consultation."
      : "A working microphone is required for this voice consultation.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "The required device is busy or unavailable. Close other camera or microphone apps, then retry.";
  }
  return "We could not start the required devices. Check your browser permissions and try again.";
}

export default function PreCallDeviceCheck({
  open = false,
  mode = "video",
  consultantReadiness = false,
  onReady,
  onCancel,
}) {
  const normalizedMode = normalizeCallMode(mode);
  const requirements = requiredMediaForMode(normalizedMode);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraIdRef = useRef("");
  const microphoneIdRef = useRef("");
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);
  const mountedRef = useRef(true);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [errorName, setErrorName] = useState("");
  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [cameraId, setCameraId] = useState("");
  const [microphoneId, setMicrophoneId] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const [selectionDirty, setSelectionDirty] = useState(false);

  const releaseDevices = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    stopStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    if (mountedRef.current) setMicLevel(0);
  }, []);

  const startMeter = useCallback((stream) => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
      const values = new Uint8Array(analyser.frequencyBinCount);
      audioContextRef.current = context;
      const tick = () => {
        analyser.getByteFrequencyData(values);
        const average = values.reduce((sum, value) => sum + value, 0) / values.length;
        if (mountedRef.current) setMicLevel(Math.min(100, Math.round(average * 1.6)));
        animationRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (_) {
      /* Meter is helpful, but not required for permission validation. */
    }
  }, []);

  const checkDevices = useCallback(async () => {
    if (!open) return;
    releaseDevices();
    setStatus("checking");
    setError("");
    setErrorName("");
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("MEDIA_UNSUPPORTED");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: microphoneIdRef.current
          ? { deviceId: { exact: microphoneIdRef.current } }
          : true,
        video: requirements.video
          ? cameraIdRef.current
            ? { deviceId: { exact: cameraIdRef.current } }
            : true
          : false,
      });
      if (!mountedRef.current) {
        stopStream(stream);
        return;
      }
      const liveAudio = stream.getAudioTracks().some((track) => track.readyState === "live");
      const liveVideo = stream.getVideoTracks().some((track) => track.readyState === "live");
      if (!liveAudio || (requirements.video && !liveVideo)) {
        stopStream(stream);
        const missing = !liveAudio ? "microphone" : "camera";
        throw new Error(`MISSING_${missing.toUpperCase()}`);
      }
      streamRef.current = stream;
      registerMediaStream(stream);
      if (requirements.video && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      startMeter(stream);
      // Live tracks are the readiness requirement. Do not make mobile users
      // wait for enumerateDevices (or reacquire media) before enabling Join.
      setSelectionDirty(false);
      setStatus("ready");

      navigator.mediaDevices
        .enumerateDevices()
        .then((listed) => {
          if (!mountedRef.current) return;
          const cameras = listed.filter((device) => device.kind === "videoinput");
          const microphones = listed.filter((device) => device.kind === "audioinput");
          setDevices({ cameras, microphones });
          if (!cameraIdRef.current && cameras[0]?.deviceId) {
            cameraIdRef.current = cameras[0].deviceId;
            setCameraId(cameras[0].deviceId);
          }
          if (!microphoneIdRef.current && microphones[0]?.deviceId) {
            microphoneIdRef.current = microphones[0].deviceId;
            setMicrophoneId(microphones[0].deviceId);
          }
        })
        .catch(() => {
          /* Device selection is optional after required tracks are live. */
        });
    } catch (err) {
      console.error("Pre-call device check failed:", err);
      if (mountedRef.current) {
        setStatus("error");
        setErrorName(err?.name || err?.message || "");
        setError(mediaErrorMessage(err, requirements.video));
      }
    }
  }, [open, releaseDevices, requirements.video, startMeter]);

  useEffect(() => {
    mountedRef.current = true;
    if (open) checkDevices();
    return () => {
      mountedRef.current = false;
      releaseDevices();
    };
  }, [open, checkDevices, releaseDevices]);

  if (!open) return null;

  const ready = status === "ready";
  const permissionBlocked = ["NotAllowedError", "PermissionDeniedError"].includes(errorName);
  const title = consultantReadiness
    ? "Get ready for instant calls"
    : normalizedMode === "voice"
      ? "Check your microphone"
      : "Check your camera and microphone";
  const detail = consultantReadiness
    ? "Camera and microphone access is required before you can be Online for video and voice consultations."
    : normalizedMode === "voice"
      ? "Only microphone permission is requested for this voice consultation."
      : "Camera and microphone are mandatory for this video consultation.";

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center overflow-y-auto bg-slate-950/90 p-3 sm:p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="p-5 sm:p-6">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[#002C6B]">
              <i className={`fa-solid ${normalizedMode === "voice" ? "fa-microphone" : "fa-video"}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{title}</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{detail}</p>
            </div>
          </div>

          {requirements.video ? (
            <div className="relative aspect-video overflow-hidden rounded-xl bg-slate-900">
              <video ref={videoRef} muted playsInline autoPlay className="h-full w-full object-cover" />
              {status !== "ready" && (
                <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-white">
                  {status === "checking" ? "Starting camera…" : "Camera preview unavailable"}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-[150px] items-center justify-center rounded-xl bg-slate-900 text-white">
              <div className="text-center">
                <i className="fa-solid fa-microphone mb-3 text-4xl" />
                <p className="text-sm font-semibold">Voice consultation — camera is not requested</p>
              </div>
            </div>
          )}

          <div className="mt-4 space-y-3">
            <div>
              <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600">
                <span>Microphone level</span>
                <span>{ready ? "Microphone ready" : "Waiting…"}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-100"
                  style={{ width: `${ready ? Math.max(4, micLevel) : 0}%` }}
                />
              </div>
            </div>

            {(devices.microphones.length > 1 || (requirements.video && devices.cameras.length > 1)) && (
              <div className="grid gap-3 sm:grid-cols-2">
                {requirements.video && devices.cameras.length > 1 && (
                  <label className="text-xs font-semibold text-slate-600">
                    Camera
                    <select
                      value={cameraId}
                      onChange={(event) => {
                        cameraIdRef.current = event.target.value;
                        setCameraId(event.target.value);
                        setSelectionDirty(true);
                      }}
                      className="mt-1 w-full rounded-lg border-slate-300 text-sm">
                      {devices.cameras.map((device, index) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Camera ${index + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {devices.microphones.length > 1 && (
                  <label className="text-xs font-semibold text-slate-600">
                    Microphone
                    <select
                      value={microphoneId}
                      onChange={(event) => {
                        microphoneIdRef.current = event.target.value;
                        setMicrophoneId(event.target.value);
                        setSelectionDirty(true);
                      }}
                      className="mt-1 w-full rounded-lg border-slate-300 text-sm">
                      {devices.microphones.map((device, index) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Microphone ${index + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}

            {selectionDirty && (
              <button
                type="button"
                onClick={checkDevices}
                className="w-full rounded-lg bg-blue-50 px-4 py-2 text-sm font-semibold text-[#002C6B] hover:bg-blue-100">
                Apply selected devices
              </button>
            )}

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <p className="font-semibold">
                  {permissionBlocked
                    ? requirements.video
                      ? "Camera and microphone are blocked"
                      : "Microphone is blocked"
                    : "We could not start your devices"}
                </p>
                <p className="mt-1">{error}</p>
                {permissionBlocked && (
                  <ol className="mt-2 list-decimal space-y-1 pl-5 text-left text-xs leading-relaxed">
                    <li>Open the lock, camera, or site-settings icon beside the browser address.</li>
                    <li>
                      Set {requirements.video ? "Camera and Microphone" : "Microphone"} to Allow for ProntoPsy.
                    </li>
                    <li>Return here and select “Retry permission check.”</li>
                  </ol>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={() => {
                releaseDevices();
                onCancel();
              }}
              className="rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100">
              Cancel
            </button>
          )}
          {!ready ? (
            <button
              type="button"
              onClick={checkDevices}
              disabled={status === "checking"}
              className="rounded-xl bg-[#002C6B] px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-60">
              {status === "checking" ? "Checking devices…" : "Retry permission check"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                saveDeviceReadiness({ audio: true, video: requirements.video });
                releaseDevices();
                onReady?.({
                  mode: normalizedMode,
                  hasMicrophone: true,
                  hasCamera: requirements.video,
                });
              }}
              className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">
              {consultantReadiness ? "Go Online — devices ready" : "Join consultation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
